import path from "path";
import { promises as fs } from "fs";

import Seo from "@/components/common/Seo";
import JsonLd, { offerListSchema } from "@/components/common/JsonLd";

import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/common/Footer";
import Header from "@/components/common/Header";
import HeroSuche from "@/components/homepage/HeroSuche";
import VergleichBlock from "@/components/sections/VergleichBlock";
import PreisSektion from "@/components/sections/PreisSektion";
import LieferwegSektion from "@/components/sections/LieferwegSektion";
import BegriffeSektion from "@/components/sections/BegriffeSektion";
import BauformenSektion from "@/components/sections/BauformenSektion";
import AusstattungSektion from "@/components/sections/AusstattungSektion";
import AblaufSektion from "@/components/sections/AblaufSektion";
import MethodikSektion from "@/components/sections/MethodikSektion";
import WizardCTA from "@/components/sections/WizardCTA";
import OccasionsList from "@/components/sections/OccasionsList";
import CitiesList from "@/components/sections/CitiesList";
import FinalCTA from "@/components/sections/FinalCTA";
import FaqSection from "@/components/sections/FaqSection";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { preisAnalyse } from "@/utils/preisAnalyse";
import { startseiteFaq } from "@/utils/startseiteFaq";
import { loadCatalog } from "@/lib/catalog";

export async function getStaticProps({ locale }) {
  const daySeed = Math.floor(Date.now() / 86400000);
  // Der vollständige Katalog, nicht mehr die sechs beliebtesten Boxen: Die
  // Startseite trägt seit dem 2026-10-07 den Vergleich selbst. Sie war mit
  // sechs Karten plus Erklärtexten für die allgemeine Suche die schwächere
  // Antwort als die eigenen Stadtseiten — Google hat auf "fotobox mieten"
  // über 25 davon rotiert, die Startseite stand auf Position 23.
  let json = { Photobooths: [] };
  if (locale === "en") {
    const datei = path.join(process.cwd(), "data", "dataEN.json");
    json = JSON.parse(await fs.readFile(datei, "utf8"));
  } else {
    json = await loadCatalog();
  }

  // Zahlen für die Vertrauenszeile im Kopf. Sie stehen bewusst nicht als
  // Text im Quelltext: "ab 99 €" ist nur so lange wahr, wie es das Paket
  // wirklich gibt.
  const alleBoxen = json.Photobooths || [];
  const paketPreise = alleBoxen.flatMap((box) => {
    const grundpreis =
      typeof box.PhotoboothPrice === "number" ? box.PhotoboothPrice : 0;
    return (box.Packages || [])
      .map(
        (paket) =>
          grundpreis +
          (typeof paket.PackagePrice === "number" ? paket.PackagePrice : 0)
      )
      .filter((preis) => preis > 0);
  });

  const katalog = {
    anzahlBoxen: alleBoxen.length,
    anzahlVersand: alleBoxen.filter((box) => box.PostDelivery).length,
    anzahlRegional: alleBoxen.filter(
      (box) => box.SelfDelivery || box.CustomerPickUp
    ).length,
    anzahlPakete: paketPreise.length,
    abPreis: paketPreise.length ? Math.min(...paketPreise) : 0,
  };

  // Grundlage für die Preisübersicht und die häufigen Fragen. Alles, was auf
  // der Seite als Zahl steht, kommt von hier — nicht aus dem Fließtext.
  const analyse = preisAnalyse(alleBoxen);

  const druckzeiten = alleBoxen
    .flatMap((box) => (box.Packages || []).map((p) => p.Printer?.PrintTimeSeconds))
    .filter((s) => typeof s === "number" && s > 0)
    .sort((a, b) => a - b);

  const merkmale = {
    anzahlBoxen: alleBoxen.length,
    anzahlAnbieter: new Set(
      alleBoxen.map((box) => box.PartnerName).filter(Boolean)
    ).size,
    spiegelreflex: alleBoxen.filter((box) =>
      /Spiegelreflex|DSLM|Systemkamera/i.test(box.CamType || "")
    ).length,
    studioblitz: alleBoxen.filter((box) => /Blitz/i.test(box.LightType || ""))
      .length,
    onlineGalerie: alleBoxen.filter((box) => box.DigitalOnlineGalerie).length,
    usb: alleBoxen.filter((box) => box.DigitalUSB).length,
    handy: alleBoxen.filter((box) => box.DigitalHandy).length,
    touch: alleBoxen.filter((box) => box.ControlTouch).length,
    liveView: alleBoxen.filter((box) => box.ControlLiveView).length,
    drucklayouts: alleBoxen.filter((box) => box.PrintLayoutsYesNo).length,
    mitFlatrate: alleBoxen.filter((box) =>
      (box.Packages || []).some((p) => p.SpecialPrintFlatrate)
    ).length,
    druckzeit: druckzeiten.length
      ? druckzeiten[Math.floor(druckzeiten.length / 2)]
      : null,
  };

  // Bauformen mit dem günstigsten echten Angebot. Kein Median: Bei zwei
  // Fotokabinen wäre das eine Zahl, die so tut, als sei sie repräsentativ.
  const bauformen = {};
  for (const box of alleBoxen) {
    const form = box.PhotoboothType;
    if (!form) continue;
    const preise = (box.Packages || [])
      .map(
        (p) =>
          (typeof box.PhotoboothPrice === "number" ? box.PhotoboothPrice : 0) +
          (typeof p.PackagePrice === "number" ? p.PackagePrice : 0)
      )
      .filter((preis) => preis > 0);
    const eintrag = bauformen[form] || { boxen: 0, min: null };
    eintrag.boxen += 1;
    if (preise.length) {
      const guenstigstes = Math.min(...preise);
      eintrag.min =
        eintrag.min === null ? guenstigstes : Math.min(eintrag.min, guenstigstes);
    }
    bauformen[form] = eintrag;
  }

  const stand = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return {
    props: {
      daySeed,
      initialProducts: json,
      katalog,
      analyse,
      merkmale,
      bauformen,
      stand,
      ...(await serverSideTranslations(locale, ["common"])),
    },
    // Wird beim Speichern im Backend gezielt neu erzeugt; die Frist ist nur
    // das Sicherheitsnetz.
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
    revalidate: 86400,
  };
}

export default function Home({
  daySeed,
  initialProducts,
  katalog,
  analyse,
  merkmale,
  bauformen,
  stand,
}) {
  const { t } = useTranslation("common");

  const metaTagTitle = t("metaTag.title");
  // Anzahl und Preis kommen aus dem Katalog. Stünden sie als Text in der
  // Übersetzung, wäre das Suchergebnis am Tag nach dem nächsten neuen
  // Anbieter falsch — und niemand würde es merken.
  const metaTagDesc = t("metaTag.desc", {
    anzahl: katalog?.anzahlBoxen || 0,
    abPreis: katalog?.abPreis || 0,
  });
  const metaTagKeywords = t("metaTag.keywords");
  const metaTagOgImageUrl = t("metaTag.ogImageUrl");
  const metaTagThumbnail = t("metaTag.thumbnail");
  return (
    <>
      <Seo
        title={metaTagTitle}
        description={metaTagDesc}
        keywords={metaTagKeywords}
        canonicalPath="/"
        image={metaTagOgImageUrl}
        alternates={{ de: "/", en: "/" }}
      >
        <meta name="thumbnail" content={metaTagThumbnail} />
      </Seo>

      {/* Die Boxen stehen dank getStaticProps im HTML — dann sollen sie auch
          als Liste ausgezeichnet sein. Ohne das ist die Startseite für Google
          Fließtext mit Bildern, nicht ein Vergleich mit Preisen. */}
      <JsonLd
        id="startseite-liste"
        data={offerListSchema(
          (initialProducts?.Photobooths || []).slice(0, 30).map((pb) => ({
            name: pb.PhotoboothName,
            partnername: pb.PartnerName,
            image: pb.PhotoboothImage,
            packages: (pb.Packages || []).map((paket) => ({
              priceValue:
                (typeof pb.PhotoboothPrice === "number"
                  ? pb.PhotoboothPrice
                  : 0) +
                (typeof paket.PackagePrice === "number"
                  ? paket.PackagePrice
                  : 0),
            })),
          })),
          {
            name: "Fotoboxen im Preisvergleich",
            path: "/",
          }
        )}
      />

      <div className="bg-[#F9F9F9] text-slate-900">
        {/* Auf dem Handy klebt der Kopf nicht: Die Seite ist seit dem
            2026-10-07 im Kern eine Liste, und darüber klebt schon die
            Filterleiste. Am Schreibtisch bleibt er, wo er war. */}
        <Header sticky="lg" />

        {/* Bewusst außerhalb von <main>: Das Hintergrundbild soll über die
            volle Breite laufen, der Inhaltsbereich ist auf 7xl begrenzt. */}
        <HeroSuche
          anzahlBoxen={katalog?.anzahlBoxen || 0}
          anzahlVersand={katalog?.anzahlVersand || 0}
          anzahlRegional={katalog?.anzahlRegional || 0}
          abPreis={katalog?.abPreis || 0}
          zielPfad="/"
          zielAnker="#vergleichsergebnisse"
          ohneHinweis
        />

        <main className="mx-auto max-w-7xl space-y-12 px-4 py-10 sm:px-6 lg:space-y-20 lg:px-8 lg:py-16">
          {/* Direkt unter den Kopf, nicht hinter zwei Erklärabschnitte: Wer
              hier ankommt, will Angebote sehen — und Google findet oben im
              HTML endlich Anbieternamen und Preise statt Fließtext.
              Seit dem 2026-10-07 der vollständige Vergleich mit Filtern statt
              sechs ausgewählter Karten: Die sechs standen Zeichen für Zeichen
              genauso auf 349 Stadtseiten, und die Startseite hatte der
              allgemeinen Suche damit nichts anzubieten, was die Stadtseiten
              nicht auch hatten. Der Ratgeber steht jetzt darunter. */}
          <section className="space-y-4">
            <div className="space-y-2">
              {/* Die Zahlen stehen hier, nicht im Kopf (Absprache
                  2026-10-07): Dort waren sie eine Behauptung über die Seite,
                  hier beschriften sie, was direkt darunter steht. Im Kopf
                  standen sie zweimal, zwei Zeilen auseinander. */}
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 lg:text-3xl">
                {`${katalog?.anzahlBoxen || 0} Fotoboxen im Preisvergleich`}
              </h2>
              <p className="max-w-2xl text-[15px] text-slate-600">
                {`ab ${katalog?.abPreis || 0} € inklusive Lieferung — ${
                  katalog?.anzahlPakete || 0
                } Pakete von ${
                  merkmale?.anzahlAnbieter || 0
                } Anbietern, nach denselben Kriterien bewertet.`}
              </p>
            </div>
            <VergleichBlock
              initialProducts={initialProducts}
              initialVisible={20}
              kopf="sortierung"
            />
          </section>

          {/* Die Reihenfolge folgt den Fragen, die ein Käufer nacheinander
              stellt: Was kostet das? Welcher Weg? Welche Bauform? Woran
              erkenne ich eine gute? Wie läuft das ab? — jede genau einmal.
              Die frühere Kachelreihe "So findest du…", die Vertrauenskacheln
              und der lange Fußtext sind darin aufgegangen; sie sagten
              dasselbe dreimal und keine Zahl dazu. */}
          <PreisSektion analyse={analyse} stand={stand} />

          <LieferwegSektion
            anzahlVersand={katalog?.anzahlVersand || 0}
            anzahlVorOrt={katalog?.anzahlRegional || 0}
          />

          <BegriffeSektion />

          <BauformenSektion bauformen={bauformen} />

          <AusstattungSektion merkmale={merkmale} />

          <AblaufSektion />

          <WizardCTA />

          <OccasionsList />

          <CitiesList />

          <MethodikSektion
            anzahlBoxen={merkmale?.anzahlBoxen || 0}
            anzahlAnbieter={merkmale?.anzahlAnbieter || 0}
          />

          <FaqSection
            title="Häufige Fragen zum Fotobox mieten"
            subtitle="Die Fragen, die uns über die Suche am häufigsten erreichen."
            items={startseiteFaq(analyse, merkmale)}
          />

          <FinalCTA />
        </main>

        <Footer />
      </div>

      <CookieBanner />
    </>
  );
}
